import { IntlShape, MessageDescriptor } from 'react-intl'

import { Programs } from './programConfigs'
import { ProgramConfigDisplayLabels, ProgramTaxonomyType } from './types'
import { Episode } from '../../models/episodes/Episodes'
import { ProgramNames } from '../../models/programs/Programs'
import { ProviderProgramTaxonomy } from '../../models/providers/Providers'
import { BRANCH_APP_LINK } from '../common/constants/constants'
import { DirectLinkIntent } from '../common/constants/customerProgram'
import { TREATMENT_OPTIONS_TYPE } from '../common/constants/treatmentOptions'
import { DirectLinkSource } from '../searchForCare/constants'

/**
 * Converts a program ID to the program name.
 *
 * This is the internal name for the program and can be used as a stable identifier.
 * For user-facing display labels, use `getProgramDisplayLabel`.
 */
export function getProgramNameFromId(programId: string | null | undefined): ProgramNames | undefined {
  return programId ? Programs.getProgramConfigById(programId)?.programName : undefined
}

export function getProgramIdFromName(programName: string | undefined): string | undefined {
  return Programs.getProgramConfigByName(programName)?.blendedCareProgramId ?? undefined
}

export function isCoachingProgram(name: ProgramNames | string | undefined): boolean {
  return Programs.getProgramConfigByName(name)?.isCoachingProgram ?? false
}

export function isDAEngageProgram(name: ProgramNames | string | undefined): boolean {
  return Programs.getProgramConfigByName(name)?.isDAEngageProgram ?? false
}

export function isDAMinorProgram(name: ProgramNames | string | undefined): boolean {
  return isDAEngageProgram(name) && isMinorProgram(name)
}

export function isChildProgram(name: ProgramNames | string | undefined): boolean {
  return Programs.getProgramConfigByName(name)?.isChildProgram ?? false
}

export function isTeenProgram(name: ProgramNames | string | undefined): boolean {
  return Programs.getProgramConfigByName(name)?.isTeenProgram ?? false
}

export function isMinorProgram(name: ProgramNames | string | undefined): boolean {
  return isChildProgram(name) || isTeenProgram(name)
}

export function isEpisodeInsightsProgram(name: ProgramNames | string | undefined): boolean {
  return Programs.getProgramConfigByName(name)?.supportsEpisodeInsights ?? false
}

export type UserConsent = {
  programName: string
  consentText: MessageDescriptor
  consentUrl: string
}

export function getConsentsInfoForUser(episodes: Episode[], isUserInternational: boolean): UserConsent[] {
  const userEpisodePrograms = [...new Set(episodes.map((episode) => episode.program_name))]

  return userEpisodePrograms.flatMap((name) => {
    const consents = Programs.getProgramConfigByName(name)?.consents ?? []
    return consents
      .filter(({ showConsentIf }) => showConsentIf({ isInternational: isUserInternational }))
      .map(({ consentText, consentUrl }) => ({ programName: name, consentText, consentUrl }))
  })
}

export function isValidCTPOCombination(ctpo?: Partial<ProviderProgramTaxonomy>): ctpo is ProviderProgramTaxonomy {
  const { clientele, treatment, partner, offering } = ctpo ?? {}
  if (!clientele || !treatment || !partner || !offering) {
    return false
  }

  const matchingPrograms = Programs.getProgramConfigsMatching(
    (program) =>
      program.treatmentOptionProperties?.programTaxonomyTreatmentOptions?.some(
        (taxonomy) =>
          taxonomy.clientele === clientele &&
          taxonomy.treatment === treatment &&
          taxonomy.partner === partner &&
          taxonomy.offering === offering,
      ) ?? false,
  )
  return matchingPrograms.length > 0
}

export function isCTPOForProgram(
  ctpo?: Partial<ProviderProgramTaxonomy> | null,
  programName?: ProgramNames | string,
): boolean {
  if (!ctpo || !programName) return false
  const programCTPO = Programs.getProgramConfigByName(programName)?.directBookingParams
  if (!programCTPO) return false
  return (
    ctpo.clientele === programCTPO.clientele &&
    ctpo.treatment === programCTPO.treatment &&
    ctpo.offering === programCTPO.offering &&
    ctpo.partner === programCTPO.partner
  )
}

export function getTriageTreatmentOptionFromCTPO(ctpo: ProviderProgramTaxonomy): TREATMENT_OPTIONS_TYPE | undefined {
  const programsWithTaxonomy = Programs.getProgramConfigsMatching(
    (program) =>
      program.treatmentOptionProperties?.programTaxonomyTreatmentOptions != null &&
      program.treatmentOptionProperties?.associatedTriageTreatmentOptions != null,
  )
  for (const program of programsWithTaxonomy) {
    const treatmentOptions = program.treatmentOptionProperties!.associatedTriageTreatmentOptions
    const taxonomies = program.treatmentOptionProperties!.programTaxonomyTreatmentOptions!
    const taxonomyIdx = taxonomies.findIndex(
      (taxonomy) =>
        taxonomy.clientele === ctpo.clientele &&
        taxonomy.treatment === ctpo.treatment &&
        taxonomy.partner === ctpo.partner &&
        taxonomy.offering === ctpo.offering,
    )
    if (taxonomyIdx >= 0) {
      if (taxonomyIdx < treatmentOptions.length) {
        return treatmentOptions[taxonomyIdx]
      }
      // associated treatment options generally match 1:1 with taxonomies
      // but individual therapy has 3 taxonomies that map to a single treatment option.
      return treatmentOptions[0]
    }
  }
  return undefined
}

export function getListOfTreatmentOptionsFromProviderTaxonomy(
  taxonomy?: ProviderProgramTaxonomy[],
): TREATMENT_OPTIONS_TYPE[] {
  if (!taxonomy) {
    return []
  }

  return taxonomy
    .map((item) => getTriageTreatmentOptionFromCTPO(item))
    .filter((option): option is TREATMENT_OPTIONS_TYPE => option !== undefined)
}

export function isCustomerProgramSelfPayEligible(customerPropertyId: string | undefined): boolean {
  return Programs.getProgramsByCustomerPropertyId(customerPropertyId).some((program) => program.selfPayEligible)
}

/**
 * Returns the display label string for a program.
 *
 * Accepts a single variant or an array of variants as a fallback chain.
 * When an array is provided, variants are tried in order and the first
 * defined value is returned. Falls back to `default` if no variant matches.
 *
 * @example
 * getProgramDisplayLabel(intl, programName, 'short')
 * getProgramDisplayLabel(intl, programName, ['daClientList', 'short'])
 *
 * See `ProgramConfigDisplayLabels` for details on each variant.
 * Only `default` and `careType` variants are internationalized.
 */
export function getProgramDisplayLabel(
  intl: IntlShape,
  programName: ProgramNames | string | undefined,
  variant: keyof ProgramConfigDisplayLabels | ReadonlyArray<keyof ProgramConfigDisplayLabels> = 'default',
): string {
  const displayLabels = Programs.getProgramConfigByName(programName)?.displayLabels
  if (!displayLabels) {
    return ''
  }

  const variants: (keyof ProgramConfigDisplayLabels)[] = Array.isArray(variant) ? variant : [variant]

  for (const v of variants) {
    if (v === 'default') {
      return intl.formatMessage(displayLabels.default)
    }
    if (v === 'careType') {
      const label = displayLabels.careType
      if (label != undefined) {
        return intl.formatMessage(label)
      }
      continue
    }
    const label = displayLabels[v]
    if (label != undefined) {
      return label
    }
  }

  return intl.formatMessage(displayLabels.default)
}

/**
 * Returns the base direct booking CTPO params for a program, looked up by program name.
 */
export function getDirectBookingParamsByProgramName(
  programName: ProgramNames | string | undefined,
): ProgramTaxonomyType | undefined {
  if (!programName) {
    return undefined
  }
  return Programs.getProgramConfigByName(programName)?.directBookingParams
}

/**
 * Builds a direct path booking deep link URL for a program.
 *
 * The base CTPO is read from `ProgramConfig.directBookingParams`. Callers supply
 * contextual overrides (intent, source) and a fallback URL.
 *
 * @returns The full deep link URL, or `undefined` if the program has no `directBookingParams`.
 */
export function buildDirectBookingLink({
  programName,
  directLinkIntent,
  directLinkSource,
  fallbackUrl,
}: {
  programName: ProgramNames | string
  directLinkIntent?: DirectLinkIntent
  directLinkSource?: DirectLinkSource
  fallbackUrl?: string
}): string | undefined {
  const params = getDirectBookingParamsByProgramName(programName)

  if (!params) return undefined

  const { clientele, treatment, partner, offering } = params

  const searchParams = new URLSearchParams()
  searchParams.set('directPath', 'true')
  searchParams.set('clientele', clientele)
  searchParams.set('treatment', treatment)
  searchParams.set('partner', partner)
  searchParams.set('offering', offering)

  if (directLinkIntent) {
    searchParams.set('directLinkIntent', directLinkIntent)
  }
  if (directLinkSource) {
    searchParams.set('directLinkSource', directLinkSource)
  }
  if (fallbackUrl) {
    searchParams.set('$fallback_url', fallbackUrl)
  }

  return `${BRANCH_APP_LINK}directPathBooking?${searchParams.toString()}`
}
